import clsx from "clsx";
import { get } from "lodash";
import React, { Fragment, useState } from "react";
import { useGraph } from "../GraphProvider/GraphProvider";
import { useRelease } from "../ReleaseProvider/ReleaseProvider";
import { useTranslate } from "../SdkProvider/SdkProvider";
import { createPluggable } from "pluggable-react";
import { COMMANDS, type GraphNode, type NodeElement } from "../../../utils";
import AttributeName from "../AttributeName/AttributeName";
import Bubble from "../../common/Bubble/Bubble";
import Chip from "../../common/Chip/Chip";
import CommandTooltip from "../CommandTooltip/CommandTooltip";
import styles from "./AttributeExplanation.module.scss";

// =============================================================================
// Types
// =============================================================================

export interface AttributeExplanationProps {
    /** The rule node to render */
    node?: GraphNode;
    /** The currently selected node to use as context for resolution */
    selectedNode?: GraphNode;
    /** Additional className */
    className?: string;
}

// =============================================================================
// Utility Components
// =============================================================================

const MathOperator = ({ text, className }: { text: string; className?: string }) => {
    if (!text) return null;
    return <span className={clsx(styles.mathOperator, className)}>{text}</span>;
};

const Bracket = ({ highlighted, depth, children }: { highlighted?: boolean; depth?: number; children: React.ReactNode }) => (
    <span
        className={styles.bracket}
        data-bracket-level={typeof depth === "number" ? depth % 6 : undefined}
        data-hovered={highlighted ? "true" : "false"}
    >
        {children}
    </span>
);

// Used for keyword values such as "Otherwise" and "Uncertain" so they read differently to literal values
const NeutralChip = ({ className, children }: { className?: string; children: React.ReactNode }) => (
    <Chip backgroundColor="#e5e7eb" color="#000" className={className}>{children}</Chip>
);

const IndentedLine = ({ depth, children, className }: { depth: number; children: React.ReactNode; className?: string }) => (
    <div className={clsx(styles.indentedLine, className)} style={{ marginLeft: depth * 8 }}>
        {children}
    </div>
);

// =============================================================================
// Function Bubble with Tooltip Description
// =============================================================================

const FunctionBubble = ({ type, children, className, value, error }: { type: string; children: React.ReactNode; className?: string; value?: any; error?: any }) => {
    return (
        <CommandTooltip type={type} value={value} error={error}>
            <Chip backgroundColor="hsl(240, 81%, 85%)" color="#000" className={clsx(styles.functionChip, className)}>
                {children}
            </Chip>
        </CommandTooltip>
    );
};

// =============================================================================
// Helper functions
// =============================================================================

const kebabToPascal = (str: string | undefined) => {
    if (!str) return "";
    const camel = str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    return camel.charAt(0).toUpperCase() + camel.slice(1);
};

// Math operators lookup table
const mathOperators: Record<string, string> = {
    plus: "+", minus: "-", multiply: "×", divide: "/", xy: "^",
    equals: "=", "not-equals": "!=", "greater-than": ">", "greater-than-equals": ">=",
    "less-than": "<", "less-than-equals": "<=", modulo: "%", trunc: "trunc", "round-up": "round-up",
};

const getNodeValidationType = (node: GraphNode) => {
    if (!node.validation) return undefined;
    return node.validation.severity === "error" ? "raise-error" : "raise-warning";
};

const ConclusionContent = ({ node, className }: { node: GraphNode; className?: string }) => {
    const validationType = getNodeValidationType(node);

    if (!validationType) {
        return <>{node.description}</>;
    }

    return (
        <>
            <FunctionBubble type={validationType} className={className}>{kebabToPascal(validationType)}</FunctionBubble>
            <Bracket>(</Bracket><MathOperator text={JSON.stringify(node.validation!.message)} /><Bracket>)</Bracket>
        </>
    );
};

// =============================================================================
// Rule Element Renderer
// =============================================================================

interface RuleComponentProps {
    element: NodeElement;
    singleline?: boolean;
    className?: string;
    depth?: number;
    path?: string;
    elementData?: any[];
    selectedNode?: GraphNode;
}

const RuleComponent = (props: RuleComponentProps) => {
    const t = useTranslate();
    const releaseContext = useRelease();
    const graphContext = useGraph();
    const graph = graphContext?.graph;
    const { element, singleline = false, className, depth = 0, path = "", elementData, selectedNode } = props;
    const [isHovered, setIsHovered] = useState(false);

    if (!element) return null;

    const match = elementData?.find((d) => d.elementPath === path);
    const value = match?.value;
    const error = match?.error;

    // Handle "empty" type (Otherwise)
    if (element.type === "empty") {
        return <NeutralChip className={className}>Otherwise</NeutralChip>;
    }

    // Handle value type
    if (element.type === "value") {
        const valueElement = element as any;
        if (valueElement.special === "BeginningOfTime") {
            return <MathOperator text={t("explanation.beginning_of_time")} className={className} />;
        } else if (valueElement.special === "EndOfTime") {
            return <MathOperator text={t("explanation.end_of_time")} className={className} />;
        }
        // A null value (e.g. "Uncertain" or a blank conclusion cell in a rule sheet) means uncertain
        if (valueElement.value === null) {
            return <NeutralChip className={className}>{t("explanation.uncertain")}</NeutralChip>;
        }
        return <MathOperator text={String(valueElement.value)} className={className} />;
    }

    // Handle attribute type
    if ("attributeId" in element && element.attributeId) {
        if (!graph) return <span className={styles.error}>No graph context</span>;
        let node = graph.node(element.attributeId) as any;
        if (!node) return <span className={styles.error}>No node found</span>;

        // Logic from old AttributeExplanation: resolve hidden nodes from selectedNode context
        if (node.hidden && graph._nodes) {
            const options = Object.values(graph._nodes).filter((n: any) => {
                if (selectedNode && n) {
                    // Check if we are in a node that matches the selected node entity
                    if (n.id === node.id && selectedNode.entity === node.entity && n.entity === node.entity && selectedNode.index === n.index) {
                        return true;
                    }
                    // Otherwise if we are on a different entity, then show all possible instances as normal
                    else if (selectedNode.entity !== n.entity && n.id === node.id && !n.hidden) {
                        return true;
                    }
                    return false;
                } else if (n && n.id === node.id && !n.hidden) {
                    return true;
                }
                return false;
            });

            // If we found a unique non-hidden match, use it
            if (options.length === 1) {
                node = options[0];
            }
        }

        return <AttributeName node={node} className={className} />;
    }

    // Handle math operators
    if (mathOperators[element.type]) {
        return (
            <span
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                data-hovered={isHovered ? "true" : "false"}
            >
                <RuleComponent element={element.elements![0]} singleline={singleline} className={className} depth={depth + 1} path={`${path}/0`} elementData={elementData} />
                <MathOperator text={mathOperators[element.type]} className={className} />
                <RuleComponent element={element.elements![1]} singleline={singleline} className={className} depth={depth + 1} path={`${path}/1`} elementData={elementData} />
            </span>
        );
    }

    // Handle AND/OR with multi-line tree indentation
    if (!singleline && (element.type === "and" || element.type === "or")) {
        return (
            <>
                <IndentedLine depth={depth}>
                    {element.type === "and" ? (
                        <Chip backgroundColor="hsl(120, 81%, 85%)" color="#000" className={className}>All</Chip>
                    ) : (
                        <Chip backgroundColor="hsl(270, 81%, 85%)" color="#000" className={className}>Any</Chip>
                    )}
                </IndentedLine>
                {element.elements?.map((child, index) => (
                    <div key={index} className={styles.treeLine} data-depth={depth + 1}>
                        <div
                            className={styles.treeLineBefore}
                            data-is-last={index === element.elements!.length - 1 ? "true" : "false"}
                            style={{ left: (depth + 1) * 8 }}
                        />
                        <div className={styles.treeContent} style={{ marginLeft: ((depth + 1) * 8) + 16 }}>
                            {child.type === "and" || child.type === "or" || child.type === "exists-scope" || child.type === "exists" ? (
                                <RuleComponent element={child} singleline={singleline} depth={depth + 1} className={className} path={`${path}/${index}`} elementData={elementData} selectedNode={selectedNode} />
                            ) : (
                                <Bubble className={className}>
                                    <RuleComponent element={child} singleline={singleline} depth={depth + 1} className={className} path={`${path}/${index}`} elementData={elementData} selectedNode={selectedNode} />
                                </Bubble>
                            )}
                        </div>
                        <div
                            className={styles.treeLineAfter}
                            data-is-not-last={index !== element.elements!.length - 1 ? "true" : "false"}
                            style={{ left: (depth + 1) * 8 }}
                        />
                    </div>
                ))}
            </>
        );
    }

    // Handle exists-scope
    if (!singleline && (element.type === "exists-scope" || element.type === "exists")) {
        const relationship = releaseContext?.release?.relationships?.find((rel) => rel.id === element.relationshipId);
        const relationshipText = relationship?.text || "";

        return (
            <>
                <IndentedLine depth={depth}>
                    <div
                        data-hovered={isHovered ? "true" : "false"}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <Bubble className={className}>
                            <FunctionBubble type={element.type} className={className} value={value} error={error}>{kebabToPascal(element.type)}</FunctionBubble>
                            <Bracket highlighted={isHovered} depth={depth}>(</Bracket>
                            <span className={styles.relationship}>{relationshipText}</span>
                            <Bracket highlighted={isHovered} depth={depth}>)</Bracket>
                        </Bubble>
                    </div>
                </IndentedLine>
                {element.elements?.map((child, index) => (
                    <div key={index} className={styles.treeLine} data-depth={depth + 1}>
                        <div
                            className={styles.treeLineBefore}
                            data-is-last={index === element.elements!.length - 1 ? "true" : "false"}
                            style={{ left: (depth + 1) * 8 }}
                        />
                        <div className={styles.treeContent} style={{ marginLeft: ((depth + 1) * 8) + 16 }}>
                            {child.type === "and" || child.type === "or" || child.type === "exists-scope" ? (
                                <RuleComponent element={child} singleline={singleline} depth={depth + 1} className={className} path={`${path}/${index}`} elementData={elementData} selectedNode={selectedNode} />
                            ) : (
                                <Bubble className={className}>
                                    <RuleComponent element={child} singleline={singleline} depth={depth + 1} className={className} path={`${path}/${index}`} elementData={elementData} selectedNode={selectedNode} />
                                </Bubble>
                            )}
                        </div>
                        <div
                            className={styles.treeLineAfter}
                            data-is-not-last={index !== element.elements!.length - 1 ? "true" : "false"}
                            style={{ left: (depth + 1) * 8 }}
                        />
                    </div>
                ))}
            </>
        );
    }

    // Handle relationship text for other function types
    let relationshipText: string | undefined;
    if ("relationshipId" in element && element.relationshipId) {
        const relationship = releaseContext?.release?.relationships?.find((rel) => rel.id === element.relationshipId);
        if (!relationship) return <span className={styles.error}>Missing relationship info</span>;
        relationshipText = relationship.text;
    }

    // Regular function rendering
    return (
        <span
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-hovered={isHovered ? "true" : "false"}
            data-deci-rule-element="true"
            data-depth={depth}
        >
            <FunctionBubble type={element.type} className={className} value={value} error={error}>{kebabToPascal(element.type)}</FunctionBubble>
            <Bracket highlighted={isHovered} depth={depth}>(</Bracket>
            {relationshipText && <span className={styles.relationship}>{relationshipText}</span>}
            {relationshipText && element.elements && element.elements.length > 0 && <span className={styles.comma}>, </span>}
            {element.elements?.map((child, index) => (
                <Fragment key={index}>
                    {index > 0 && <span className={styles.comma}>, </span>}
                    <RuleComponent element={child} singleline={singleline} depth={depth + 1} className={className} path={`${path}/${index}`} elementData={elementData} selectedNode={selectedNode} />
                </Fragment>
            ))}
            <Bracket highlighted={isHovered} depth={depth}>)</Bracket>
        </span>
    );
};

// =============================================================================
// Rule Table (for decision tables)
// =============================================================================

const RuleTable = ({ node, className, selectedNode }: { node: GraphNode; className?: string; selectedNode?: GraphNode }) => {
    const t = useTranslate();
    return (
        <>
            <p>{t("explanation.decison_table_heading")}</p>
            <table className={styles.table}>
                <thead className={styles.tableHead}>
                    <tr>
                        <th className={styles.headerCell}>{t("explanation.decision_table_value_of")}</th>
                        <th className={styles.headerCell}>{t("explanation.decision_table_conditions_met")}</th>
                    </tr>
                </thead>
                <tbody>
                    {node.rows?.map((row, index) => (
                        <tr key={index}>
                            <td className={styles.tableCell}>
                                <RuleComponent element={get(node as any, `value.${index}`)} singleline={false} className={className} path={`values/${index}`} depth={0} elementData={(node as any).debugInfo?.elementData} selectedNode={selectedNode} />
                            </td>
                            <td className={styles.tableCell}>
                                <RuleComponent element={get(node as any, `rows.${index}.0`)} singleline={false} className={className} path={`rows/${index}/0`} depth={0} elementData={(node as any).debugInfo?.elementData} selectedNode={selectedNode} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

// =============================================================================
// Main AttributeExplanation Component
// =============================================================================

const DefaultAttributeExplanation = (props: AttributeExplanationProps) => {
    const { node, className, selectedNode } = props;
    const t = useTranslate();

    if (!node) return null;

    let content: React.ReactNode = null;

    // Decision table
    if (node.rows) {
        content = <RuleTable node={node} className={className} selectedNode={selectedNode} />;
    }
    // Conditional rule
    else if (node.conditions) {
        content = (
            <>
                <div className={styles.conclusionWrap}>
                    <Bubble variant="conclusion" className={className}>
                        <ConclusionContent node={node} className={className} />
                    </Bubble>
                    <Chip backgroundColor="hsl(240, 81%, 85%)" color="#000" className={styles.ifChip}>if</Chip>
                </div>
                <div>
                    {!["and", "or", "exists-scope", "exists"].includes(node.conditions[0].type) ? (
                        <IndentedLine depth={1}>
                            <Bubble className={className}>
                                <RuleComponent element={node.conditions[0]} singleline={false} depth={1} className={className} path="conditions/0" elementData={(node as any).debugInfo?.elementData} selectedNode={selectedNode} />
                            </Bubble>
                        </IndentedLine>
                    ) : (
                        <RuleComponent element={node.conditions[0]} singleline={false} depth={1} className={className} path="conditions/0" elementData={(node as any).debugInfo?.elementData} selectedNode={selectedNode} />
                    )}
                </div>
            </>
        );
    }
    // Simple value assignment
    else if (node.value) {
        const simpleValue = Array.isArray(node.value) ? node.value[0] : node.value;
        content = (
            <Bubble variant="conclusion" className={className}>
                <ConclusionContent node={node} className={className} /> = <RuleComponent element={simpleValue} singleline={true} className={className} path="value" depth={0} elementData={(node as any).debugInfo?.elementData} selectedNode={selectedNode} />
            </Bubble>
        );
    }
    // Input attribute (no rule)
    else {
        content = <span>{t("explanation.input_node")}</span>;
    }

    return (
        <div className={clsx(styles.attributeExplanation, className)} data-deci-attribute-explanation="true">
            {content}
        </div>
    );
};

// Create pluggable component
const AttributeExplanation = createPluggable<AttributeExplanationProps>("deci.attributeExplanation", DefaultAttributeExplanation);

export default Object.assign(AttributeExplanation, {
    RuleComponent: RuleComponent,
});
